import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../api/axios';
import toast from 'react-hot-toast';
import {
  MdArrowBack, MdArticle, MdAccountBalanceWallet, MdMonetizationOn,
  MdPendingActions, MdVisibility, MdSearch, MdTrendingUp,
} from 'react-icons/md';
import GlobalDateFilter from '../components/GlobalDateFilter';
import Pagination from '../components/Pagination';
import ContributorDetailModal from '../components/ContributorDetailModal';
import usePagination from '../hooks/usePagination';
import { formatDate, formatDateTime, formatINR, statusBadgeClass } from '../utils/format';

const startOfDay = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };
const endOfDay = (d) => { const x = new Date(d); x.setHours(23, 59, 59, 999); return x; };

/** Converts a GlobalDateFilter selection into concrete from/to Date objects for listArticles' from/to params. */
const dateRangeFromFilter = (filter, fromDate, toDate) => {
  const today = new Date();
  switch (filter) {
    case 'today': return { from: startOfDay(today), to: endOfDay(today) };
    case 'yesterday': { const y = new Date(today); y.setDate(y.getDate() - 1); return { from: startOfDay(y), to: endOfDay(y) }; }
    case 'last7': { const s = new Date(today); s.setDate(s.getDate() - 6); return { from: startOfDay(s), to: endOfDay(today) }; }
    case 'last30': { const s = new Date(today); s.setDate(s.getDate() - 29); return { from: startOfDay(s), to: endOfDay(today) }; }
    case 'thisMonth': { const s = new Date(today.getFullYear(), today.getMonth(), 1); return { from: startOfDay(s), to: endOfDay(today) }; }
    case 'lastMonth': { const s = new Date(today.getFullYear(), today.getMonth() - 1, 1); const e = new Date(today.getFullYear(), today.getMonth(), 0); return { from: startOfDay(s), to: endOfDay(e) }; }
    case 'custom': return { from: fromDate ? startOfDay(fromDate) : null, to: toDate ? endOfDay(toDate) : null };
    default: return { from: null, to: null };
  }
};

function StatCard({ icon, label, value, color }) {
  return (
    <div className="card" style={{ display: 'flex', alignItems: 'center', gap: 14, padding: 18 }}>
      <div style={{ width: 44, height: 44, borderRadius: 10, background: `${color}1A`, color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, flexShrink: 0 }}>
        {icon}
      </div>
      <div>
        <div style={{ fontSize: 12, color: 'var(--text-secondary)', fontWeight: 500 }}>{label}</div>
        <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--text)' }}>{value}</div>
      </div>
    </div>
  );
}

export default function AuthorDetail() {
  const { type, id } = useParams(); // 'contributor' | 'admin'
  const navigate = useNavigate();
  const isContributor = type === 'contributor';

  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [walletStats, setWalletStats] = useState(null);
  const [adminStats, setAdminStats] = useState(null);
  const [withdrawals, setWithdrawals] = useState([]);
  const [showContributorModal, setShowContributorModal] = useState(false);

  const handleOpenContributorModal = async () => {
    setShowContributorModal(true);
    try {
      const [cRes, wRes] = await Promise.all([
        api.get(`/admin/contributors/${id}`),
        api.get(`/admin/contributors/${id}/wallet`).catch(() => null),
      ]);
      if (cRes.data?.data) {
        setProfile(cRes.data.data);
      }
      if (wRes?.data?.data?.stats) {
        setWalletStats(wRes.data.data.stats);
      }
    } catch (e) {
      // Best-effort refresh; profile is already populated from initial load
    }
  };

  const [categories, setCategories] = useState([]);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const [customRange, setCustomRange] = useState({ from: '', to: '' });

  const [posts, setPosts] = useState([]);
  const { page, limit, setPage, setLimit } = usePagination(15);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [postsLoading, setPostsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        if (isContributor) {
          const [cRes, wRes, wdRes] = await Promise.all([
            api.get(`/admin/contributors/${id}`),
            api.get(`/admin/contributors/${id}/wallet`),
            api.get(`/admin/contributors/${id}/withdrawals`),
          ]);
          if (cancelled) return;
          setProfile(cRes.data.data);
          setWalletStats(wRes.data.data.stats);
          setWithdrawals(wdRes.data.data || []);
        } else {
          const res = await api.get(`/admin/admins/${id}/stats`);
          if (cancelled) return;
          setProfile(res.data.data.admin);
          setAdminStats(res.data.data);
        }
      } catch (err) {
        toast.error('Failed to load author details');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [type, id, isContributor]);

  useEffect(() => {
    api.get('/categories').then(res => setCategories(res.data.data)).catch(() => {});
  }, []);

  const fetchPosts = useCallback(async (fetchPage = page, fetchLimit = limit) => {
    setPostsLoading(true);
    try {
      const { from, to } = dateRangeFromFilter(dateFilter, customRange.from, customRange.to);
      const params = { page: fetchPage, limit: fetchLimit, status: 'published' };
      if (isContributor) params.contributor_id = id; else params.admin_id = id;
      if (category) params.category = category;
      if (search.trim()) params.search = search.trim();
      if (from) params.from = from.toISOString();
      if (to) params.to = to.toISOString();
      const res = await api.get('/admin/articles', { params });
      setPosts(res.data.data);
      setPagination(res.data.pagination);
    } catch (err) {
      toast.error('Failed to load posts');
    } finally {
      setPostsLoading(false);
    }
  }, [id, isContributor, category, search, dateFilter, customRange, page, limit]);

  useEffect(() => {
    fetchPosts(page, limit);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, limit]);

  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) { isFirstRender.current = false; return; }
    const t = setTimeout(() => {
      if (page === 1) fetchPosts(1, limit); else setPage(1);
    }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category, search, dateFilter, customRange]);

  if (loading) return <div className="page-loader">Loading...</div>;
  if (!profile) {
    return (
      <div className="page">
        <button className="btn btn-ghost" onClick={() => navigate(-1)} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginBottom: 16 }}>
          <MdArrowBack /> Back
        </button>
        <p className="text-muted">Author not found.</p>
      </div>
    );
  }

  return (
    <div className="page">
      <button className="btn btn-ghost" onClick={() => navigate(-1)} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginBottom: 16 }}>
        <MdArrowBack /> Back
      </button>

      <div className="page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          {profile.image ? (
            <img src={profile.image} alt={profile.name} style={{ width: 56, height: 56, borderRadius: '50%', objectFit: 'cover' }} />
          ) : (
            <div style={{ width: 56, height: 56, borderRadius: '50%', background: '#E0E7FF', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, fontWeight: 700, color: '#4F46E5' }}>
              {(profile.name || '?')[0]}
            </div>
          )}
          <div>
            <h2 style={{ margin: 0 }}>{profile.name}</h2>
            <p className="text-muted" style={{ margin: 0 }}>
              {isContributor ? 'Contributor' : (profile.role === 'admin' ? 'Admin' : 'Reporter')}
              {profile.email ? ` · ${profile.email}` : ''}
              {profile.phone ? ` · ${profile.phone}` : ''}
            </p>
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: isContributor ? 'repeat(auto-fit, minmax(200px, 1fr))' : 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 24 }}>
        {isContributor ? (
          <>
            <StatCard icon={<MdArticle />} label="Total Posts" value={pagination.total} color="#2563EB" />
            <StatCard icon={<MdAccountBalanceWallet />} label="Wallet Balance" value={formatINR(walletStats?.available_balance || 0)} color="#059669" />
            <StatCard icon={<MdMonetizationOn />} label="Total Earned" value={formatINR(walletStats?.income_till_date || 0)} color="#7C3AED" />
            <StatCard icon={<MdPendingActions />} label="Pending Withdrawal" value={formatINR(walletStats?.pending_withdrawal || 0)} color="#D97706" />
          </>
        ) : (
          <>
            <StatCard icon={<MdArticle />} label="Total Posts" value={adminStats?.totalArticles || 0} color="#2563EB" />
            <StatCard icon={<MdTrendingUp />} label="Posts Today" value={adminStats?.articlesToday || 0} color="#059669" />
            <StatCard icon={<MdVisibility />} label="Total Views" value={adminStats?.totalViews || 0} color="#7C3AED" />
          </>
        )}
      </div>

      {isContributor && (
        <div
          className="card"
          style={{
            marginBottom: 24,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 16,
            padding: '16px 22px',
            borderLeft: '4px solid #2563EB',
          }}
        >
          <div>
            <h4 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: 'var(--text)' }}>
              Contributor Information
            </h4>
            <p className="text-muted" style={{ margin: '4px 0 0 0', fontSize: 13 }}>
              View full profile details, assigned location, contact info, and KYC verification for {profile.name}.
            </p>
          </div>
          <button
            className="btn btn-primary"
            onClick={handleOpenContributorModal}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              padding: '9px 18px',
              fontWeight: 600,
              fontSize: 14,
              cursor: 'pointer',
            }}
          >
            <MdVisibility size={18} /> View Contributor Information
          </button>
        </div>
      )}

      {isContributor && (
        <div className="card" style={{ marginBottom: 24 }}>
          <div className="section-header">
            <h3>Withdrawal History</h3>
          </div>
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Amount</th>
                  <th>Status</th>
                  <th className="hide-mobile">Requested</th>
                  <th className="hide-mobile">Reference</th>
                </tr>
              </thead>
              <tbody>
                {withdrawals.map(w => (
                  <tr key={w.id}>
                    <td style={{ fontWeight: 600 }}>{formatINR(w.amount)}</td>
                    <td><span className={`badge ${statusBadgeClass(w.status)}`}>{w.status}</span></td>
                    <td className="hide-mobile">{formatDateTime(w.created_at || w.createdAt)}</td>
                    <td className="hide-mobile">{w.payout_reference || '—'}</td>
                  </tr>
                ))}
                {withdrawals.length === 0 && <tr><td colSpan="4" className="empty-row">No withdrawals yet</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="page-header" style={{ paddingTop: 0 }}>
        <h3 style={{ margin: 0 }}>Posts ({pagination.total})</h3>
      </div>

      <div className="filter-bar">
        <div className="search-input-wrap">
          <MdSearch className="search-icon" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search posts by title..."
            className="search-input"
          />
        </div>
        <select value={category} onChange={(e) => setCategory(e.target.value)}>
          <option value="">All Categories</option>
          {categories.map(c => <option key={c.slug} value={c.name}>{c.name}</option>)}
        </select>
        <GlobalDateFilter
          filter={dateFilter || 'today'}
          fromDate={customRange.from}
          toDate={customRange.to}
          onChange={(f, from, to) => { setDateFilter(f); setCustomRange({ from, to }); }}
        />
        {dateFilter && (
          <button className="btn btn-sm btn-ghost" onClick={() => { setDateFilter(''); setCustomRange({ from: '', to: '' }); }}>
            Clear Date
          </button>
        )}
      </div>

      {postsLoading ? <div className="page-loader">Loading...</div> : (
        <>
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th className="hide-mobile">Image</th>
                  <th>Title</th>
                  <th className="hide-mobile">Category</th>
                  <th className="hide-mobile">Shares</th>
                  <th className="hide-mobile">Views</th>
                  <th className="hide-mobile">Likes</th>
                  <th>Published</th>
                </tr>
              </thead>
              <tbody>
                {posts.map(p => {
                  const imageSrc = p.thumbnail_url || p.image_url || '/placeholder.png';
                  return (
                    <tr key={p.id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/articles/engagement/${p.id}`)}>
                      <td className="hide-mobile" style={{ textAlign: 'center', padding: '8px' }}>
                        <img src={imageSrc} alt={p.title} style={{ width: '56px', height: '56px', objectFit: 'cover', borderRadius: '8px' }} onError={(e) => e.target.style.display = 'none'} />
                      </td>
                      <td className="title-cell"><span className="article-title">{p.title}</span></td>
                      <td className="hide-mobile">{p.category?.replace('_', ' ')}</td>
                      <td className="hide-mobile">
                        <span
                          style={{
                            fontSize: '12px',
                            color: '#059669',
                            background: '#ECFDF5',
                            padding: '4px 8px',
                            borderRadius: '6px',
                            fontWeight: '600',
                            display: 'inline-block',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          ↗️ {p.share_count || 0} Shares
                        </span>
                      </td>
                      <td className="hide-mobile">{p.view_count}</td>
                      <td className="hide-mobile">{p.like_count}</td>
                      <td>{formatDate(p.published_at)}</td>
                    </tr>
                  );
                })}
                {posts.length === 0 && <tr><td colSpan="7" className="empty-row">No posts found</td></tr>}
              </tbody>
            </table>
          </div>
          <Pagination pagination={pagination} onPageChange={setPage} limit={limit} onLimitChange={setLimit} />
        </>
      )}

      {showContributorModal && isContributor && (
        <ContributorDetailModal
          detail={profile}
          wallet={{ stats: walletStats }}
          onClose={() => setShowContributorModal(false)}
          onContributorUpdated={(updated) => setProfile((prev) => ({ ...prev, ...updated }))}
        />
      )}
    </div>
  );
}
